// src/components/settings/CityManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { settingsService } from '../../services/settingsService';
import LoadingSpinner from '../common/LoadingSpinner';
import Toast from '../common/Toast';
import Pagination from '../common/Pagination';
import Modal from '../common/Modal';

const CityManagement = () => {
    const [cities, setCities] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCountry, setFilterCountry] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingCity, setEditingCity] = useState(null);
    const [formData, setFormData] = useState({ name: '', latitude: '', longitude: '', country_id: '' });
    const [formErrors, setFormErrors] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1
    });

    const loadCountries = useCallback(async () => {
        try {
            const response = await settingsService.getCountries({ page: 1, limit: 1000 });
            setCountries(response.countries || []);
        } catch (error) {
            console.error('Error loading countries:', error);
        }
    }, []);

    const loadCities = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.current_page,
                limit: pagination.per_page,
                search: searchTerm,
                country_id: filterCountry
            };
            const response = await settingsService.getCities(params);
            setCities(response.cities || []);
            if (response.pagination) {
                setPagination(response.pagination);
            }
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination.current_page, pagination.per_page, searchTerm, filterCountry]);

    useEffect(() => {
        loadCountries();
    }, [loadCountries]);

    useEffect(() => {
        loadCities();
    }, [loadCities]);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
    };

    const handleAddClick = () => {
        setEditingCity(null);
        setFormData({ name: '', latitude: '', longitude: '', country_id: '' });
        setFormErrors({});
        setShowModal(true);
    };

    const handleEditClick = (city) => {
        setEditingCity(city);
        setFormData({
            name: city.name || '',
            latitude: city.latitude || '',
            longitude: city.longitude || '',
            country_id: city.country_id || ''
        });
        setFormErrors({});
        setShowModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'City name is required';
        if (!formData.latitude.trim()) errors.latitude = 'Latitude is required';
        if (!formData.longitude.trim()) errors.longitude = 'Longitude is required';
        if (!formData.country_id) errors.country_id = 'Country is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSaveLoading(true);
            if (editingCity) {
                await settingsService.updateCity(editingCity.id, formData);
                showToast('City updated successfully', 'success');
            } else {
                await settingsService.createCity(formData);
                showToast('City created successfully', 'success');
            }
            setShowModal(false);
            loadCities();
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this city?')) return;

        try {
            await settingsService.deleteCity(id);
            showToast('City deleted successfully', 'success');
            loadCities();
        } catch (error) {
            showToast(error.message, 'error');
        }
    };

    const getCountryName = (countryId) => {
        const country = countries.find(c => c.id === countryId);
        return country ? country.name : '-';
    };

    return (
        <div>
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: 'success' })}
            />

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                        City Management
                    </h2>
                    <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '13px' }}>
                        Manage cities in the system
                    </p>
                </div>
                <button
                    onClick={handleAddClick}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fas fa-plus"></i>
                    Add City
                </button>
            </div>

            {/* Filters */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <input
                    type="text"
                    placeholder="Search cities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        padding: '10px 15px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px'
                    }}
                />
                <select
                    value={filterCountry}
                    onChange={(e) => setFilterCountry(e.target.value)}
                    style={{
                        padding: '10px 15px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px'
                    }}
                >
                    <option value="">All Countries</option>
                    {countries.map(country => (
                        <option key={country.id} value={country.id}>{country.name}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <LoadingSpinner />
                </div>
            ) : cities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <i className="fas fa-city" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}></i>
                    <h3>No cities found</h3>
                    <p>Try adjusting your filters or add a new city</p>
                </div>
            ) : (
                <>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>ID</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Name</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Country</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Latitude</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Longitude</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#333', fontSize: '13px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cities.map((city) => (
                                    <tr key={city.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{city.id}</td>
                                        <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>{city.name}</td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{getCountryName(city.country_id)}</td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{city.latitude}</td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{city.longitude}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleEditClick(city)}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: '#007bff',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    marginRight: '8px'
                                                }}
                                            >
                                                <i className="fas fa-edit"></i> Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(city.id)}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: '#dc3545',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                <i className="fas fa-trash"></i> Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div style={{ marginTop: '20px' }}>
                        <Pagination
                            currentPage={pagination.current_page}
                            totalPages={pagination.total_pages}
                            onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
                        />
                    </div>
                </>
            )}

            {/* Add/Edit Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingCity ? 'Edit City' : 'Add City'}
                size="medium"
            >
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            City Name <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.name ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                            placeholder="Enter city name"
                        />
                        {formErrors.name && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.name}</div>}
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Country <span style={{ color: 'red' }}>*</span>
                        </label>
                        <select
                            name="country_id"
                            value={formData.country_id}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.country_id ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        >
                            <option value="">Select country</option>
                            {countries.map(country => (
                                <option key={country.id} value={country.id}>{country.name}</option>
                            ))}
                        </select>
                        {formErrors.country_id && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.country_id}</div>}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                                Latitude <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="text"
                                name="latitude"
                                value={formData.latitude}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: formErrors.latitude ? '1px solid #dc3545' : '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                                placeholder="e.g., 24.8607"
                            />
                            {formErrors.latitude && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.latitude}</div>}
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                                Longitude <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="text"
                                name="longitude"
                                value={formData.longitude}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: formErrors.longitude ? '1px solid #dc3545' : '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                                placeholder="e.g., 67.0011"
                            />
                            {formErrors.longitude && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.longitude}</div>}
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#6c757d',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                            disabled={saveLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#007bff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: saveLoading ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                opacity: saveLoading ? 0.7 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                            disabled={saveLoading}
                        >
                            {saveLoading && <LoadingSpinner size="small" />}
                            {saveLoading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default CityManagement;
